import React, { useState, useEffect } from 'react';
import { createicrc1Actor } from "../ic/icpswap/icrc1/index.js";

// Helper function to convert size prop to actual dimensions
const getSizeDimensions = (size) => {
  // Handle both new style prop and legacy size string
  if (typeof size === 'object' && size.width && size.height) {
    return { width: size.width, height: size.height };
  }
  
  // Legacy size string handling (e.g., "w-5 h-5")
  const sizeMap = {
    'w-3 h-3': { width: '0.75rem', height: '0.75rem' },
    'w-4 h-4': { width: '1rem', height: '1rem' },
    'w-5 h-5': { width: '1.25rem', height: '1.25rem' },
    'w-6 h-6': { width: '1.5rem', height: '1.5rem' },
    'w-8 h-8': { width: '2rem', height: '2rem' },
    'w-10 h-10': { width: '2.5rem', height: '2.5rem' },
    'w-12 h-12': { width: '3rem', height: '3rem' },
    'w-16 h-16': { width: '4rem', height: '4rem' }
  };
  
  return sizeMap[size] || { width: '1.25rem', height: '1.25rem' };
};

export default function TokenIcon({ 
  tokenCanisterId, 
  size = "w-5 h-5", 
  style = {},
  showFallback = true 
}) {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (tokenCanisterId) {
      fetchTokenLogo(tokenCanisterId);
    }
  }, [tokenCanisterId]);

  const fetchTokenLogo = async (canisterId) => {
    console.log("logo token")
    try {
      let actor = createicrc1Actor(canisterId)

      const metadata = await actor.icrc1_metadata();
      console.log("metadata", metadata)
      // Look for logo in metadata
      const logoEntry = metadata.find(([key]) => key === 'icrc1:logo');
      console.log("logo entry", logoEntry)
      if (logoEntry && logoEntry[1].Text) {
        // If logo is a data URL or HTTP URL, use it directly
        const logoData = logoEntry[1].Text;
        if (logoData.startsWith('data:') || logoData.startsWith('http')) {
          setLogoUrl(logoData);
        } else {
          // If it's base64 encoded, create a data URL
          setLogoUrl(`data:image/svg+xml;base64,${logoData}`);
        }
      } else {
        // Fallback to ICPSwap URL if no logo in metadata
        setLogoUrl(`https://app.icpswap.com/images/tokens/${canisterId}.svg`);
      }
    } catch (error) {
      console.log(`Could not fetch metadata for ${canisterId}, using fallback:`, error);
      // Fallback to ICPSwap URL on error
      setLogoUrl(`https://app.icpswap.com/images/tokens/${canisterId}.svg`);
    } finally {
      setLoading(false);
    }
  };

  const getFallbackText = (canisterId) => {
    const tokenMap = {
      'ryjl3-tyaaa-aaaaa-aaaba-cai': 'ICP',
      '4zmwe-paaaa-aaaam-qdwxa-cai': 'DV',
      'icaf7-3aaaa-aaaam-qcx3q-cai': 'RG',
      '7pail-xaaaa-aaaas-aabmq-cai': 'BOB',
      'iwv6l-6iaaa-aaaal-ajjjq-cai': 'CL',
      '6yaih-jyaaa-aaaab-aaxia-cai': 'VL'
    };
    return tokenMap[canisterId?.toString()] || '?';
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  // Get dimensions based on size prop
  const dimensions = getSizeDimensions(size);
  
  // Base styles for the container
  const baseContainerStyle = {
    width: dimensions.width,
    height: dimensions.height,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    ...style
  };

  // Show loading state while fetching metadata
  if (loading) {
    return (
      <div style={{
        ...baseContainerStyle,
        backgroundColor: '#e5e7eb',
        animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
      }}>
        <div style={{
          width: '0.75rem',
          height: '0.75rem',
          backgroundColor: '#9ca3af',
          borderRadius: '50%'
        }}></div>
      </div>
    );
  }

  // Show fallback if no logo URL or error occurred
  if (imageError || !logoUrl || !tokenCanisterId) {
    if (!showFallback) return null;
    
    return (
      <div style={{
        ...baseContainerStyle,
        background: 'linear-gradient(to right, #3b82f6, #8b5cf6)',
        color: '#ffffff',
        fontWeight: '700',
        fontSize: '0.75rem'
      }}>
        {getFallbackText(tokenCanisterId)}
      </div>
    );
  }

  return (
    <div style={baseContainerStyle}>
      {!imageLoaded && showFallback && (
        <div style={{
          width: dimensions.width,
          height: dimensions.height,
          borderRadius: '50%',
          background: 'linear-gradient(to right, #3b82f6, #8b5cf6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#ffffff',
          fontWeight: '700',
          fontSize: '0.75rem',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0
        }}>
          {getFallbackText(tokenCanisterId)}
        </div>
      )}
      <img
        src={logoUrl}
        alt={`${getFallbackText(tokenCanisterId)} token`}
        style={{
          width: dimensions.width,
          height: dimensions.height,
          borderRadius: '50%',
          objectFit: 'cover',
          transition: 'opacity 200ms ease',
          opacity: imageLoaded ? 1 : 0
        }}
        onError={handleImageError}
        onLoad={handleImageLoad}
        loading="lazy"
      />
    </div>
  );
}
